import { ThemeSearchParams, ThemeSearchResponse, themeOverview } from '@api';
import { useDebounce } from '@helpers';
import { useQuery } from '@tanstack/react-query';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

export const SEARCH_THEMES_QUERY_KEY = 'SEARCH_THEMES_QUERY_KEY';

const DEBOUNCE_MS = 300;

export interface UseSearchThemesProps extends ThemeSearchParams {
  enabled?: boolean;
}

export const useSearchThemes = ({
  q = '',
  types,
  limit,
  enabled = true,
}: UseSearchThemesProps) => {
  const trimmedQuery = q.trim();
  const debouncedQuery = useDebounce(trimmedQuery, DEBOUNCE_MS);
  const typesKey = types?.join(',') ?? '';
  // A query awaiting debounce must not fall back to the `''` key, or the
  // cached empty-query defaults render as if they were results.
  const searchQuery = trimmedQuery === '' ? '' : debouncedQuery || trimmedQuery;

  // Empty `q` still hits the endpoint (server returns defaults)
  const canSearch = enabled && (trimmedQuery === '' || !!debouncedQuery);

  const result = useQuery({
    queryKey: [SEARCH_THEMES_QUERY_KEY, searchQuery, typesKey, limit],
    queryFn: ({ signal }) =>
      wretchRequest<ThemeSearchResponse>(
        themeOverview.searchThemes.path({
          q: searchQuery,
          types,
          limit,
        }),
        {
          schema: themeOverview.searchThemes.schema,
          signal,
        }
      ),
    enabled: canSearch,
    // Keep prior hits while the query string debounces, but not across `types`
    // changes (avoids flashing industry themes on the macro tab, etc.) and not
    // from the empty-query defaults, which aren't results for any search.
    placeholderData: (previousData, previousQuery) => {
      const [, previousSearch, previousTypes] = previousQuery?.queryKey ?? [];

      return !!previousSearch && previousTypes === typesKey
        ? previousData
        : undefined;
    },
  });

  return {
    ...result,
    data: result.data ?? [],
    debouncedQuery,
  };
};
